// Booking-request stats charts, shared by the Stats tab, the portfolio
// analytics card, and the owner-portal dashboard section (lazy-loaded there to
// keep Recharts out of the owner bundle's critical path).
//
// Colors ride the CVD-validated --status-* / --chart-* tokens from index.css
// so both themes work without any JS theme plumbing; identity never rides
// color alone (legend + direct counts everywhere).
import {
  PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, ResponsiveContainer,
} from "recharts";
import type { BookingStatsResponse } from "@shared/booking-stats";

export const BOOKING_STATUS_CHART_COLORS: Record<string, string> = {
  approved: "hsl(var(--status-approved))",
  rejected: "hsl(var(--status-rejected))",
  archived: "hsl(var(--status-archived))",
};

const GROUP_SERIES_COLORS = [
  "hsl(var(--chart-1))",
  "hsl(var(--chart-2))",
  "hsl(var(--chart-3))",
  "hsl(var(--chart-4))",
  "hsl(var(--chart-5))",
];

/** Stable color for a time-series key: status keys use the status set; grouped
 * (property/template) keys take --chart-N in the server's fixed top-5 order;
 * the "other" rollup is always the neutral gray. */
export function seriesColor(key: string, index: number): string {
  if (BOOKING_STATUS_CHART_COLORS[key]) return BOOKING_STATUS_CHART_COLORS[key];
  if (key === "other" || key === "none") return "hsl(var(--status-archived))";
  return GROUP_SERIES_COLORS[index % GROUP_SERIES_COLORS.length];
}

const STATUS_LABELS: Record<string, string> = {
  approved: "Approved",
  rejected: "Rejected",
  archived: "Archived",
};

export function BookingStatusPie({ byStatus, decided }: {
  byStatus: BookingStatsResponse["byStatus"];
  decided: number;
}) {
  const slices = byStatus.filter((s) => s.count > 0);
  return (
    <div className="flex items-center gap-6" data-testid="booking-status-pie">
      <div className="relative shrink-0">
        <PieChart width={170} height={170}>
          <Pie
            data={slices}
            dataKey="count"
            nameKey="status"
            innerRadius={52}
            outerRadius={80}
            paddingAngle={2}
            strokeWidth={0}
          >
            {slices.map((s) => (
              <Cell key={s.status} fill={BOOKING_STATUS_CHART_COLORS[s.status]} />
            ))}
          </Pie>
          <Tooltip
            formatter={(value: number, name: string) => [value, STATUS_LABELS[name] ?? name]}
            contentStyle={{ fontSize: 12 }}
          />
        </PieChart>
        <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
          <div className="text-2xl font-bold" data-testid="text-pie-decided">{decided}</div>
          <div className="text-xs text-muted-foreground">decided</div>
        </div>
      </div>
      <div className="space-y-2">
        {byStatus.map((s) => (
          <div key={s.status} className="flex items-center gap-2 text-sm" data-testid={`legend-status-${s.status}`}>
            <span
              className="inline-block h-3 w-3 rounded-sm shrink-0"
              style={{ backgroundColor: BOOKING_STATUS_CHART_COLORS[s.status] }}
            />
            <span className="text-muted-foreground">{STATUS_LABELS[s.status] ?? s.status}</span>
            <span className="font-medium">{s.count}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export function BookingStatsTimeChart({ timeSeries, height = 260, onSeriesClick }: {
  timeSeries: BookingStatsResponse["timeSeries"];
  height?: number;
  /** Optional drill-through: fired with the clicked series key. */
  onSeriesClick?: (seriesKey: string) => void;
}) {
  const data = timeSeries.buckets.map((b) => ({ label: b.label, ...b.values }));
  return (
    <div style={{ width: "100%", height }} data-testid="booking-stats-time-chart">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data} margin={{ top: 8, right: 8, bottom: 0, left: -16 }}>
          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="hsl(var(--border))" />
          <XAxis dataKey="label" tick={{ fontSize: 11 }} tickLine={false} axisLine={false} interval="preserveStartEnd" />
          <YAxis allowDecimals={false} tick={{ fontSize: 11 }} tickLine={false} axisLine={false} width={40} />
          <Tooltip contentStyle={{ fontSize: 12 }} cursor={{ fill: "hsl(var(--muted))", opacity: 0.4 }} />
          <Legend wrapperStyle={{ fontSize: 12 }} />
          {timeSeries.seriesKeys.map((s, i) => (
            <Bar
              key={s.key}
              dataKey={s.key}
              name={s.label}
              stackId="decisions"
              fill={seriesColor(s.key, i)}
              stroke="hsl(var(--background))"
              strokeWidth={1}
              maxBarSize={48}
              onClick={onSeriesClick ? () => onSeriesClick(s.key) : undefined}
              cursor={onSeriesClick ? "pointer" : undefined}
            />
          ))}
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}
